import { Minus, Plus } from "lucide-react";
import { saleUnitLabel } from "../lib/itemUnits";
import { useI18n } from "../lib/i18n";

// Item purchase and negotiation forms keep raw text so users can clear/retype.
// Button steps clamp to stock bounds; callers must validate the draft before
// submitting, because typing intentionally does not coerce invalid quantities.
export function QuantityInput({ id, value, max, disabled = false, onChange, unit }: {
  id: string;
  value: string;
  max: number;
  disabled?: boolean;
  unit?: string;
  onChange: (value: string) => void;
}) {
  const { t } = useI18n();
  const quantity = Number(value);
  const valid = Number.isSafeInteger(quantity) && quantity >= 1 && quantity <= max;
  function step(delta: number) {
    const current = Number.isSafeInteger(quantity) ? quantity : 1;
    onChange(String(Math.max(1, Math.min(max, current + delta))));
  }

  return <div className="flex h-11 w-44 max-w-full items-center overflow-hidden rounded-xl border border-line bg-white text-navy focus-within:border-navy-500 focus-within:ring-2 focus-within:ring-navy-500/10">
    <button type="button" disabled={disabled || max < 1 || quantity <= 1} onClick={() => step(-1)} aria-label={t("detail.item.decreaseQuantity")} className="grid h-11 w-11 shrink-0 place-items-center hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-40"><Minus className="h-4 w-4" /></button>
    <input id={id} type="number" inputMode="numeric" required min={1} max={max} step={1} value={value} disabled={disabled} aria-label={`${t("detail.item.quantity")}${unit ? ` (${saleUnitLabel(unit, "2")})` : ""}`} aria-invalid={value !== "" && !valid} onChange={(event) => onChange(event.target.value)} className="h-full min-w-0 flex-1 bg-transparent text-center text-sm font-semibold outline-none disabled:opacity-60 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
    <button type="button" disabled={disabled || max < 1 || quantity >= max} onClick={() => step(1)} aria-label={t("detail.item.increaseQuantity")} className="grid h-11 w-11 shrink-0 place-items-center hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-40"><Plus className="h-4 w-4" /></button>
  </div>;
}
